import React from "react";
import { AVATAR_HERO, CATEGORIES, PERSONA_NAME } from "../lib/constants";
import { askQuestion, getHistory } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShareButtons } from "./ShareButtons";
import { LanguagePicker } from "./LanguagePicker";
import { AiEngineToggle } from "./AiEngineToggle";
import { Send, Loader2, Quote, History, Crown, Languages, Cpu } from "lucide-react";
import { toast } from "sonner";

export const AskSection = React.forwardRef(({ presetProblem, onAnswered }, ref) => {
  const { user, setAuthOpen, setUpgradeOpen, freeAnonQuestions } = useAuth();
  const [handle, setHandle] = React.useState("");
  const [category, setCategory] = React.useState("General");
  const [problem, setProblem] = React.useState("");
  const [language, setLanguage] = React.useState("English");
  const [model, setModel] = React.useState("gemini");
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [history, setHistory] = React.useState([]);

  const premium = !!user?.premium;

  const loadHistory = React.useCallback(async () => {
    if (!premium) {
      setHistory([]);
      return;
    }
    try {
      setHistory(await getHistory());
    } catch {
      setHistory([]);
    }
  }, [premium]);

  React.useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  React.useImperativeHandle(ref, () => ({ refreshHistory: loadHistory }), [loadHistory]);

  React.useEffect(() => {
    if (presetProblem) setProblem(presetProblem.replace(/\u200b/g, ""));
  }, [presetProblem]);

  const submit = async (e) => {
    e.preventDefault();
    if (problem.trim().length < 4) {
      toast.error("Abeg, tell Shabba Robo a little more.");
      return;
    }
    // Registration gate: anonymous visitors get a couple of free questions, then must register.
    if (!user) {
      const used = parseInt(localStorage.getItem("shabba_anon_asks") || "0", 10) || 0;
      if (used >= (freeAnonQuestions || 2)) {
        toast.info("You've used your free questions. Create a free account to keep asking Shabba Robo.");
        setAuthOpen(true);
        return;
      }
    }
    setLoading(true);
    setResult(null);
    try {
      const data = await askQuestion({ handle: handle.trim() || "Anonymous", category, problem: problem.trim(), language, model });
      setResult(data);
      if (!user) {
        const used = parseInt(localStorage.getItem("shabba_anon_asks") || "0", 10) || 0;
        localStorage.setItem("shabba_anon_asks", String(used + 1));
      }
      onAnswered && onAnswered();
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.detail === "register_required") {
        localStorage.setItem("shabba_anon_asks", String(freeAnonQuestions || 2));
        toast.info("You've used your free questions. Create a free account to keep asking Shabba Robo.");
        setAuthOpen(true);
      } else {
        toast.error(err?.response?.data?.detail || "Shabba Robo is resting. Try again shortly.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="ask" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 scroll-mt-20">
      <div className="max-w-2xl">
        <span className="text-sm uppercase tracking-[0.2em] font-bold text-clay">Ask Shabba Robo</span>
        <h2 className="font-display font-black text-ink text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">
          Pour out your mind. Get real answers.
        </h2>
        <p className="text-ink/70 mt-4 leading-relaxed">
          No judgment, no wahala. Ask your first couple of questions free — create a quick account to
          keep going. {PERSONA_NAME} responds with wisdom, wit and workable steps.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mt-10">
        {/* Form */}
        <form onSubmit={submit} className="bg-sand-light border border-border rounded-3xl p-6 sm:p-8 shadow-[0_8px_30px_rgba(212,93,60,0.06)]" data-testid="ask-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-ink/70">Your handle (optional)</label>
              <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="e.g. Lagos_Boy" className="mt-2 w-full rounded-xl bg-sand border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30 focus:border-clay transition-colors" data-testid="ask-handle-input" />
            </div>
            <div>
              <label className="text-sm font-semibold text-ink/70">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-2 w-full rounded-xl bg-sand border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30 focus:border-clay transition-colors" data-testid="ask-category-select">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="text-sm font-semibold text-ink/70">What's on your mind?</label>
            <textarea value={problem} onChange={(e) => setProblem(e.target.value)} rows={6} placeholder="Share your problem, worry or question..." className="mt-2 w-full rounded-xl bg-sand border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30 focus:border-clay transition-colors resize-none" data-testid="ask-problem-input" />
          </div>

          <div className="mt-4">
            <label className="text-sm font-semibold text-ink/70 flex items-center gap-2"><Languages size={15} className="text-clay" /> Answer language</label>
            <div className="mt-2">
              <LanguagePicker value={language} onChange={setLanguage} />
            </div>
          </div>

          <div className="mt-4">
            <label className="text-sm font-semibold text-ink/70 flex items-center gap-2"><Cpu size={15} className="text-clay" /> AI engine</label>
            <div className="mt-2">
              <AiEngineToggle value={model} onChange={setModel} />
            </div>
          </div>

          <button type="submit" disabled={loading} className="mt-5 w-full rounded-full bg-clay text-white font-bold px-6 py-4 hover:bg-clay-hover transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 flex items-center justify-center gap-2" data-testid="ask-submit-btn">
            {loading ? <><Loader2 size={18} className="animate-spin" /> Shabba Robo dey think...</> : <><Send size={18} /> Get my answer</>}
          </button>
        </form>

        {/* Answer */}
        <div className="relative" data-testid="ask-answer-panel">
          {!result && !loading && (
            <div className="h-full min-h-[280px] rounded-3xl border-2 border-dashed border-border flex flex-col items-center justify-center text-center p-8">
              <img src={AVATAR_HERO} alt="" className="w-20 h-20 rounded-full object-cover ring-2 ring-clay/30" />
              <p className="text-ink/50 mt-4 max-w-xs">Your answer from {PERSONA_NAME} will appear right here.</p>
            </div>
          )}
          {loading && (
            <div className="h-full min-h-[280px] rounded-3xl bg-sand-light border border-border flex flex-col items-center justify-center p-8">
              <Loader2 size={32} className="animate-spin text-clay" />
              <p className="text-ink/60 mt-4">Cooking wisdom just for you...</p>
            </div>
          )}
          {result && (
            <div className="fade-up rounded-3xl bg-ink text-sand-light p-6 sm:p-8 shadow-[0_20px_50px_rgba(26,28,41,0.25)]">
              <div className="flex items-center gap-3">
                <img src={AVATAR_HERO} alt={PERSONA_NAME} className="w-12 h-12 rounded-full object-cover ring-2 ring-ochre/60" />
                <div>
                  <p className="font-display font-bold text-lg text-ochre">{PERSONA_NAME}</p>
                  <p className="text-xs text-sand-light/60">{result.category}</p>
                </div>
              </div>
              <Quote size={28} className="text-clay mt-6" />
              <p className="mt-3 text-base sm:text-lg leading-relaxed whitespace-pre-line text-sand-light/90" data-testid="ask-answer-text">{result.answer}</p>
              <div className="mt-6 pt-5 border-t border-white/10">
                <ShareButtons text={result.answer.slice(0, 220)} label="Share this answer" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Premium history */}
      {user && (
        <div className="mt-10">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-display font-bold text-xl text-ink flex items-center gap-2">
              <History size={20} className="text-clay" /> Your question history
              <span className="inline-flex items-center gap-1 text-xs text-ochre"><Crown size={12} /> Premium</span>
            </h3>
          </div>
          {premium ? (
            history.length === 0 ? (
              <p className="text-ink/50 mt-4">No saved questions yet. Ask something above and it will be saved here.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4 mt-4" data-testid="history-list">
                {history.map((h) => (
                  <div key={h.id} className="bg-sand-light border border-border rounded-2xl p-5" data-testid={`history-item-${h.id}`}>
                    <span className="text-xs font-bold uppercase tracking-wider text-clay bg-clay/10 rounded-full px-3 py-1">{h.category}</span>
                    <p className="font-display font-bold text-ink mt-3">“{h.problem}”</p>
                    <p className="text-sm text-ink/70 mt-2 line-clamp-4 whitespace-pre-line">{h.answer}</p>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="mt-4 rounded-2xl border border-border bg-sand-light p-6 flex items-center justify-between flex-wrap gap-4" data-testid="history-locked">
              <p className="text-ink/60">Saving your history is a premium feature. Subscribe to keep every answer in one place.</p>
              <button onClick={() => setUpgradeOpen(true)} className="rounded-full bg-clay text-white font-bold px-6 py-3 hover:bg-clay-hover transition-transform hover:-translate-y-0.5 flex items-center gap-2" data-testid="history-upgrade-btn">
                <Crown size={16} /> Go Premium
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
});

AskSection.displayName = "AskSection";
